/* ==========================================================================
   Vanguarda — size guide inside the quick-add sheet.

   The sheet in vg-quick-add.js lets a shopper pick colour and size without
   leaving the cart. What it could not answer was the question that actually
   stalls the decision: "is a Medium my Medium?". Sending them to the product
   page for the chart undoes the whole point of the sheet.

   This file adds two things to the sheet, and only to the sheet:

     1. a Size guide button in the size option's label row
     2. the measurements for the chosen size, written out underneath the size
        buttons the moment one is pressed

   It is a separate file on purpose. vg-quick-add.js builds its markup by
   assigning innerHTML, so anything added here would be wiped on the next
   redraw; instead of forking that file, this one watches the sheet's body and
   re-applies itself after every redraw. That keeps the two concerns apart and
   means the sheet still works, unchanged, if this file fails to load.

   The tables come from the #VGSizeTables JSON block that quick-add.liquid
   prints — the same numbers as the full guide in snippets/size-chart.liquid.
   They are BODY measurements in US sizing, not the measured dimensions of the
   garment, and the note under the table says so.
   ========================================================================== */
(function () {
  'use strict';

  var unit = 'in';       // 'in' | 'cm', remembered while the drawer is open
  var tables = null;     // parsed #VGSizeTables
  var watcher = null;    // the MutationObserver, paused while we write

  function tablesFor() {
    if (tables) return tables;
    var el = document.getElementById('VGSizeTables');
    try { tables = el ? JSON.parse(el.textContent) : {}; }
    catch (err) { tables = {}; }
    return tables;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /* The sheet is built from /products/<handle>.js, which vg-quick-add.js keeps
     to itself. Rather than fetch the product a second time, read what the
     shopper can already see: the option block whose label says "size", and the
     values printed on its buttons. */
  function sizeBlock() {
    var body = document.getElementById('QuickAddBody');
    if (!body) return null;
    var opts = body.querySelectorAll('.qa__opt');
    for (var i = 0; i < opts.length; i++) {
      var lab = opts[i].querySelector('.qa__lab b');
      if (lab && String(lab.textContent).toLowerCase().indexOf('size') > -1) {
        return opts[i];
      }
    }
    return null;
  }

  function valuesIn(block) {
    var out = [];
    block.querySelectorAll('.qa__val').forEach(function (b) {
      out.push(b.getAttribute('data-val'));
    });
    return out;
  }

  function chosenIn(block) {
    var on = block.querySelector('.qa__val.is-on, .qa__val[aria-pressed="true"]');
    return on ? on.getAttribute('data-val') : null;
  }

  /* Which chart this product uses, decided the same way the product page
     decides it: the size values first, then the product type. A shoe sized
     "US 9" and a short sized 34" share nothing with a knit sized S–XL. */
  function chartKey(values) {
    var all = values.join(',');
    if (all.indexOf('US ') > -1) return 'shoes';
    if (all.indexOf('"') > -1) return 'waist';

    var t = tablesFor();
    // Letter sizes: tops and bottoms use the same letters, so the product's
    // own words are what separate them. The sheet's title is what we have.
    var title = document.getElementById('QuickAddTitle');
    var s = title ? String(title.textContent).toLowerCase() : '';
    if (/trouser|pant|jogger|chino|short|jean|cargo|legging|skirt/.test(s)) return 'bottoms';
    return t.tops ? 'tops' : null;
  }

  function rowFor(key, size) {
    var t = tablesFor()[key];
    if (!t || !size) return null;
    for (var i = 0; i < t.rows.length; i++) {
      if (String(t.rows[i][0]) === String(size)) return { cols: t.cols, row: t.rows[i] };
    }
    return null;
  }

  // "38–40|97–102" -> "38–40 in" / "97–102 cm"
  function cell(v) {
    var parts = String(v).split('|');
    return (unit === 'cm' ? (parts[1] || parts[0]) : parts[0]) + ' ' + unit;
  }

  /* ------------------------------------------------------------- injection */
  /* Our own writes are mutations too. A flag is not enough to stop the loop —
     observer callbacks arrive after the current task, by which time any flag
     has been cleared — so the observer is detached for the duration instead. */
  function apply() {
    var body = document.getElementById('QuickAddBody');
    if (watcher) watcher.disconnect();
    try { applyInner(); }
    finally { if (watcher && body) watcher.observe(body, { childList: true, subtree: true }); }
  }

  function applyInner() {
    var block = sizeBlock();
    if (!block) return;

    var values = valuesIn(block);
    var key = chartKey(values);
    if (!key || !tablesFor()[key]) return;

    {
      // 1. the trigger, in the label row where the doubt happens
      var lab = block.querySelector('.qa__lab');
      if (lab && !lab.querySelector('[data-qa-guide]')) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'qa__guide-open';
        btn.setAttribute('data-qa-guide', '');
        btn.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" ' +
          'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<rect x="2" y="7" width="20" height="10" rx="1"/>' +
          '<path d="M6 7v3M10 7v4M14 7v3M18 7v4"/></svg>Size guide';
        var tail = lab.querySelector('i');
        if (tail) lab.insertBefore(btn, tail); else lab.appendChild(btn);
      }

      // 2. the measurements for the size under the shopper's thumb
      var size = chosenIn(block);
      var meas = block.querySelector('.qa__meas');
      if (!meas) {
        meas = document.createElement('div');
        meas.className = 'qa__meas';
        var vals = block.querySelector('.qa__vals');
        if (vals && vals.parentNode) vals.parentNode.insertBefore(meas, vals.nextSibling);
        else block.appendChild(meas);
      }

      var mr = rowFor(key, size);
      if (!mr) {
        meas.innerHTML = '<span class="qa__meas-empty">Pick a size to see its measurements.</span>';
      } else {
        var h = '<dl>';
        for (var c = 1; c < mr.cols.length; c++) {
          h += '<div><dt>' + esc(mr.cols[c]) + '</dt><dd>' + esc(cell(mr.row[c])) + '</dd></div>';
        }
        h += '</dl><span class="qa__meas-src">Body measurements, US sizing &middot; size ' +
             esc(size) + '</span>';
        meas.innerHTML = h;
      }
    }
  }

  /* ----------------------------------------------------------- the guide */
  function drawGuide() {
    var box = document.getElementById('QuickAddGuideBody');
    var block = sizeBlock();
    if (!box || !block) return;

    var mine = valuesIn(block).map(String);
    var key = chartKey(mine);
    var t = tablesFor()[key];
    if (!t) { box.innerHTML = ''; return; }
    var size = chosenIn(block);

    var lede = key === 'shoes'
      ? 'Measure your foot at the end of the day, standing, heel against a wall.'
      : key === 'tops'
        ? 'Find your chest measurement first — on a top, chest decides the size and everything else follows.'
        : 'Measure where you actually wear the waistband, not at the narrowest point.';

    var h = '<p class="qa__guide-lede">' + lede + '</p>' +
      '<div class="qa__units">' +
        '<button type="button" data-qa-unit="in" aria-pressed="' + (unit === 'in') + '">in</button>' +
        '<button type="button" data-qa-unit="cm" aria-pressed="' + (unit === 'cm') + '">cm</button>' +
      '</div><table class="qa__table"><thead><tr>';
    t.cols.forEach(function (c) { h += '<th scope="col">' + esc(c) + '</th>'; });
    h += '</tr></thead><tbody>';

    t.rows.forEach(function (r) {
      var has = mine.indexOf(String(r[0])) > -1;
      var on = String(size) === String(r[0]);
      h += '<tr class="' + (has ? 'is-in' : 'is-out') + (on ? ' is-here' : '') + '">' +
           '<th scope="row">' + esc(r[0]) + '</th>';
      for (var c = 1; c < r.length; c++) h += '<td>' + esc(cell(r[c])) + '</td>';
      h += '</tr>';
    });

    h += '</tbody></table>' +
         '<p class="qa__guide-note">Highlighted rows are the sizes this piece is made in. ' +
         'These are body measurements in US sizing, not the measured dimensions of the garment.</p>';
    box.innerHTML = h;
  }

  function openGuide() {
    var wrap = document.getElementById('QuickAddGuide');
    if (!wrap) return;
    drawGuide();
    wrap.hidden = false;
    requestAnimationFrame(function () { wrap.classList.add('is-open'); });
  }

  function closeGuide() {
    var wrap = document.getElementById('QuickAddGuide');
    if (!wrap) return;
    wrap.classList.remove('is-open');
    wrap.hidden = true;
  }

  /* ------------------------------------------------------------------ wire */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    if (e.target.closest('[data-qa-guide]')) { e.preventDefault(); openGuide(); return; }
    if (e.target.closest('[data-qa-guide-close]')) { e.preventDefault(); closeGuide(); return; }

    var u = e.target.closest('[data-qa-unit]');
    if (u) {
      e.preventDefault();
      unit = u.getAttribute('data-qa-unit');
      drawGuide();
      apply();
      return;
    }

    // Closing the sheet takes the guide with it, so it is not left open over
    // whatever the shopper opens next.
    if (e.target.closest('[data-qa-close]') || e.target.closest('[data-quick-view]')) {
      closeGuide();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var wrap = document.getElementById('QuickAddGuide');
    // Escape peels one layer at a time: the guide first, the sheet after.
    if (wrap && !wrap.hidden) { closeGuide(); e.stopPropagation(); }
  }, true);

  /* The sheet rewrites its own body on every choice. Watch for that and put
     the guide button and the measurements back, rather than patching the file
     that does the rewriting. */
  function watch() {
    var body = document.getElementById('QuickAddBody');
    if (!body || body.dataset.vgGuide) return;
    body.dataset.vgGuide = '1';
    watcher = new MutationObserver(function () { apply(); });
    watcher.observe(body, { childList: true, subtree: true });
    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
  // The cart drawer is re-rendered by the theme on every cart change; the sheet
  // itself sits outside that, but this keeps us safe if it ever moves.
  document.addEventListener('shopify:section:load', watch);
})();
